import React, { Fragment, useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

import HeaderComponent from "../../components/HeaderComponent";
import TitleH2Component from "../../components/TitleH2Component";
import InputComponent from "../../components/InputComponent";
import LoaderComponent from "../../components/LoaderComponent";
import AlertComponent from "../../components/AlertComponent";
import ModalGeneralComponent from "../../components/ModalGeneralComponent";
import MoneyInputComponent from "../../components/MoneyInputComponent";
import PaginadorComponent from "../../components/PaginadorComponent";
import { useFechaActual } from "../../hooks/useFechaActual";
import { useEstadoAgenda } from "../../hooks/useEstadoAgenda";
import stylebtn from "../../styles/btn.module.scss";

import {
  getAgenda,
  postAgenda,
  putAgenda,
  postActividades,
  putActividades,
  deleteAgenda,
  loginNotasPrivadas,
} from "../../api/Agenda";
import ParrafoComponent from "../../components/ParrafoComponent";
import { useTokenValidator } from "../../hooks/useTokenValidator";
import { useAuth } from "../../context/AuthContext";

const NotasAgregadasPages = () => {
  const { obtenerEstadoPrioritario } = useEstadoAgenda();
  const navigate = useNavigate();
  const location = useLocation();
  // ---------------------------- //
  // Estados principales
  // ---------------------------- //
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [alert, setAlert] = useState(false);
  const [errors, setErrors] = useState({});
  const { isTokenValid } = useTokenValidator();

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [tipo, setTipo] = useState("agenda");
  const [step, setStep] = useState(1);
  const [showModalLogin, setShowModalLogin] = useState(false);

  // Eliminación
  const [showEliminar, setShowEliminar] = useState(false);
  const [agendaToDelete, setAgendaToDelete] = useState(null);

  // Ids para edición
  const [agendaId, setAgendaId] = useState(null);
  const [actividadId, setActividadId] = useState(null);

  const [pin, setPin] = useState("");
  const { isAdmin } = useAuth();
  const handleChangePin = (e) => {
    setPin(e.target.value);
  };

  // Formularios
  const [formDataAgenda, setFormDataAgenda] = useState({
    nombre: "",
    celular: "",
    tipo: "",
  });
  const [formDataActividad, setFormDataActividad] = useState({
    monto: "",
    descripcion: "",
    fecha_caducidad: "",
  });

  // Paginación
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 7;

  const pageCount = Math.ceil(data.length / itemsPerPage);
  const currentPageData = data.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  const [busqueda, setBusqueda] = useState("");
  // ---------------------------- //
  // Mensajes
  // ---------------------------- //

  const handleChangeBusqueda = (event) => {
    setBusqueda(event.target.value);
  };
  const showMensaje = (msg, duracion = 3000) => {
    setMensaje(msg);
    setAlert(true);
    setTimeout(() => {
      setMensaje("");
      setAlert(false);
    }, duracion);
  };

  const [searchParams] = useSearchParams();
  const notaPrivada = searchParams.get("tipo") === "true";

  const handleCloseModalLogin = () => {
    if (!notaPrivada) {
      setShowModalLogin(false);
    } else {
      window.location.href = "/home";
    }
  };
  useEffect(() => {
    const tokenNotas = sessionStorage.getItem("tokenNotas");
    // Validar el token
    const { valid, decoded } = isTokenValid(tokenNotas);
    if ((!tokenNotas || !valid || !decoded?.name) && notaPrivada) {
      setShowModalLogin(true);
    } else {
      setShowModalLogin(false);
    }
  }, [notaPrivada]);

  // ---------------------------- //
  // Fetch de datos
  // ---------------------------- //
  const fetchData = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await getAgenda(filters);
      const agendas = res?.data || [];

      // Calcular el estado prioritario de cada agenda
      const agendasConEstado = agendas.map((agenda) => {
        // Ajusta aquí la propiedad que contiene las actividades si es necesario:
        // puede ser agenda.Actividades, agenda.actividades, agenda.detalles, etc.
        const actividades = agenda.actividades

        const { estado, bg } = obtenerEstadoPrioritario(actividades);

        return { ...agenda, estadoPrioritario: estado, bgPrioritario: bg };
      });

      // IMPORTANTE: guardar los datos transformados en el estado
      setData(agendasConEstado);
    } catch (err) {
      console.error("Error cargando agendas:", err);
      showMensaje("Error cargando datos");
    } finally {
      setLoading(false);
    }

  };



  // ---------------------------- //
  // Efectos
  // ---------------------------- //

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("search") || "";
    const filters = {};
    if (!notaPrivada) filters.isPrivada = false;

    if (query) {
      setBusqueda(query);
      filters.searchQuery = query;
    }

    fetchData(filters);
  }, [notaPrivada, location.search]);

  // ---------------------------- //
  // Buscar
  // ---------------------------- //
  const buscarNotas = (e) => {
    e.preventDefault();
    const filters = {
      searchQuery: busqueda,
    };

    if (!notaPrivada) {
      filters.isPrivada = false; // en públicas, restringir
    } else {
      filters.isPrivada = true;
    }

    fetchData(filters);
    setCurrentPage(0);
    navigate(`?tipo=${notaPrivada}&search=${busqueda}`);
  };

  // ---------------------------- //
  // Inputs
  // ---------------------------- //
  const handleInputChangeAgenda = (e) => {
    const { name, value } = e.target;
    setFormDataAgenda((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChangeActividad = (e) => {
    const { name, value } = e.target;
    setFormDataActividad((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------------------- //
  // Modal Crear / Editar
  // ---------------------------- //
  const handleShowModal = () => {
    setShowEdit(false);
    setStep(1);
    setFormDataAgenda({ nombre: "", celular: "", tipo: "agenda" });
    setFormDataActividad({ monto: "", descripcion: "", fecha_caducidad: "" });
    setTipo("agenda");
    setAgendaId(null);
    setActividadId(null);
    setShowModal(true);
  };

  const handleShowEditar = (item) => {
    setShowEdit(true);
    setShowModal(true);
    setStep(1);
    setTipo(item.tipo || "agenda");
    setFormDataAgenda({
      nombre: item.nombre || "",
      celular: item.celular || "",
      tipo: item.tipo || "agenda",
    });
    setFormDataActividad({
      monto: item.monto || "",
      descripcion: item.descripcion || "",
      fecha_caducidad: item.fecha_caducidad || "",
    });
    setAgendaId(item.id);
    if (item.actividadId) setActividadId(item.actividadId);
  };

  const handleCloseModal = () => setShowModal(false);

  // ---------------------------- //
  // Guardar / Editar Agenda
  // ---------------------------- //
  const handleSaveAgenda = async (e) => {
    e.preventDefault();
    const iduser = localStorage.getItem("iduser");
    const payload = {
      ...formDataAgenda,
      tipo,
      id_user: iduser,
      privada: notaPrivada,
    };

    let newErrors = {};

    if (!payload.nombre) newErrors.nombre = "El nombre es obligatorio *";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      const res = showEdit
        ? await putAgenda(agendaId, payload)
        : await postAgenda(payload);
      if (res.success) {
        if (!showEdit) setAgendaId(res.data.id);
        setStep(!showEdit ? 2 : 1);
        if (showEdit) {
          showMensaje("Registro actualizado correctamente✅");
          handleCloseModal();
          const filters = {};
          const params = new URLSearchParams(location.search);
          const query = params.get("search") || "";
          if (query) {
            setBusqueda(query);
            filters.searchQuery = query;
          }
          if (notaPrivada) {
            filters.isPrivada = true;
          } else {
            filters.isPrivada = false;
          }
          fetchData(filters);
        }
      }
    } catch (err) {
      console.error("Error guardando agenda:", err);
      showMensaje("Error guardando agenda");
    }
  };

  // ---------------------------- //
  // Guardar / Editar Actividad
  // ---------------------------- //
  const handleSaveActividad = async (e) => {
    e.preventDefault();

    const payload = {
      id_agenda: agendaId, // 👈 este es requerido
      monto: tipo === "agenda" ? formDataActividad.monto : undefined, // 👈 se ignora si es recordatorio
      descripcion: formDataActividad.descripcion,
      fecha_caducidad: formDataActividad.fecha_caducidad,
      tipo,
    };

    let newErrors = {};
    //    if (!payload.monto) newErrors.monto = "El monto es obligatorio *";
    if (!payload.descripcion)
      newErrors.descripcion = "La Descripcion es obligatorio *";
    if (!payload.fecha_caducidad)
      newErrors.fecha_caducidad = "La fecha de caducidad es obligatorio *";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      const res = showEdit
        ? await putActividades(actividadId, payload)
        : await postActividades(payload); // 👈 aquí mandas el objeto completo

      if (res.success) {
        showMensaje("Registrado creado con éxito");
        handleCloseModal();
        const filters = {};
        if (notaPrivada) {
          filters.isPrivada = true;
        } else {
          filters.isPrivada = false;
        }
        fetchData(filters);

        // limpiar formularios
        setFormDataAgenda({ nombre: "", celular: "", tipo: "agenda" });
        setFormDataActividad({
          monto: "",
          descripcion: "",
          fecha_caducidad: "",
        });
        setStep(1);
        setAgendaId(null);
      }
    } catch (err) {
      console.error("Error guardando actividad:", err);
      showMensaje("Error guardando actividad");
    }
  };

  // ---------------------------- //
  // Eliminar
  // ---------------------------- //
  const handleShowEliminar = (item) => {
    setAgendaToDelete(item.id);
    setShowEliminar(true);
  };
  const handleCloseEliminar = () => setShowEliminar(false);

  const eliminarRegistro = async () => {
    try {
      const res = await deleteAgenda(agendaToDelete);
      if (res.success) {
        showMensaje("Registro eliminado con éxito");
        handleCloseEliminar();
        const filters = {};
        const params = new URLSearchParams(location.search);
        const query = params.get("search") || "";
        if (query) {
          setBusqueda(query);
          filters.searchQuery = query;
        }
        if (notaPrivada) {
          filters.isPrivada = true;
        } else {
          filters.isPrivada = false;
        }
        fetchData(filters);
        setCurrentPage(0);

      }
    } catch (err) {
      console.error("Error eliminando registro:", err);
      showMensaje("Error eliminando registro");
    }
  };

  //Login  con pin
  const loginPin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const id = localStorage.getItem("iduser");

    if (pin === "" || !id) {
      setMensaje("El campo está vacío");
      setLoading(false);
      return;
    }

    const res = await loginNotasPrivadas(id, pin);

    if (!res.success) {
      setMensaje(res.data.msg || "Error en login");
    } else {
      sessionStorage.setItem("tokenNotas", res.data.token);
      setShowModalLogin(false);
    }
    setLoading(false);
  };

  const salirPrivate = async () => {
    sessionStorage.removeItem("tokenNotas");
    window.location.href = "/home";
  };

  // ---------------------------- //
  // Render
  // ---------------------------- //
  return (
    <Fragment>
      <div className="d-flex justify-content-center">
        <AlertComponent
          mensaje={mensaje}
          activaalert={alert}
          styleGroup="bg-success color-white"
        />
      </div>

      <HeaderComponent>
        <TitleH2Component styleGroup="box-text color-dark fw700 roboto mb-4">
          AGENDA
        </TitleH2Component>
        <button
          type="button"
          onClick={handleShowModal}
          className={`${stylebtn.btn} ${stylebtn.primary}`}
        >
          CREAR NOTA
        </button>

        {notaPrivada === true ? (
          <button
            type="button"
            onClick={salirPrivate}
            className={`${stylebtn.btn} ${stylebtn.danger} ms-3`}
          >
            SALIR
          </button>
        ) : null}
      </HeaderComponent>

      {loading ? (
        <LoaderComponent styleGroup="mt-5" />
      ) : (
        <div className="conatiner-global pt-3 mb-5">
          {/* Búsqueda */}
          <section>
            <div className="container">
              <form onSubmit={buscarNotas}>
                <div className="row gy-4 d-flex justify-content-end align-items-end">
                  <div className="col-12 col-md-6 col-xl-3">
                    <InputComponent
                      placeholder="Buscar nota"
                      label="Buscar Nota"
                      styleLabel="fw600 WorkSans"
                      htmlFor="bloque"
                      type="search"
                      value={busqueda}
                      onChange={handleChangeBusqueda}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-xl-2 d-flex justify-content-start">
                    <button
                      type="submit"
                      className={`${stylebtn.btn} ${stylebtn.primary} mt-4`}
                    >
                      BUSCAR
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </section>

          {/* Tabla */}
          <section>
            <div className="table-responsive mt-4">
              <table className="table table-bordered tableGeneral">
                <thead>
                  <tr>
                    <th scope="col" className="color-madera">
                      #
                    </th>
                    <th scope="col" className="color-madera">
                      Nombre/Titulo
                    </th>
                    <th scope="col" className="color-madera">
                      Celular
                    </th>
                    <th scope="col" className="color-madera">
                      Tipo
                    </th>
                    <th scope="col" className="color-madera">
                      Estado
                    </th>
                    <th scope="col" className="color-madera">
                      ver
                    </th>
                    {isAdmin && <th scope="col" className="color-madera">
                      Editar
                    </th>}
                    {isAdmin && <th scope="col" className="color-madera">
                      Eliminar
                    </th>}
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.length > 0 ? (
                    currentPageData.map((item, index) => (
                      <tr key={item.id}>
                        <th>{index + 1}</th>
                        <td>{item.nombre}</td>
                        <td>{item.celular}</td>
                        <td>{item.tipo}</td>
                        <td
                          className={`text-nowrap ${item.bgPrioritario || ""
                            } text-center`}
                        >
                          {item.estadoPrioritario || "Sin Notas"}
                        </td>
                        <td className="text-center">
                          <Link
                            to={`/detalle-notas/${item.id}`}
                            className={`${stylebtn.btn_desnudo} ${stylebtn.icon} ms-3 p-0 d-inline-flex`}
                          >
                            <Icon icon="mdi:file-search-outline" />
                          </Link>
                        </td>
                        {isAdmin && <td className="text-center">
                          <Link
                            onClick={() => handleShowEditar(item)}
                            className={`${stylebtn.btn_desnudo} ${stylebtn.icon} ms-3 p-0 d-inline-flex`}
                          >
                            <Icon icon="raphael:edit" />
                          </Link>
                        </td>}
                        {isAdmin && <td className="text-center">
                          <button
                            onClick={() => handleShowEliminar(item)}
                            className={`${stylebtn.btn_desnudo} ${stylebtn.icon} ms-3 p-0 d-inline-flex color-danger`}
                          >
                            <Icon icon="majesticons:delete-bin" />
                          </button>
                        </td>}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        SIN AGENDA
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {pageCount > 1 && (
            <PaginadorComponent
              setCurrentPage={setCurrentPage}
              pages={pageCount}
              styleGroup="d-flex justify-content-center pt-4"
            />
          )}
        </div>
      )}

      {/* Modal Crear/Editar */}
      <ModalGeneralComponent
        ModalHeader
        titulo={`${showEdit ? "EDITAR" : "AGREGAR"} NOTA`}
        styleGroup="d-flex align-items-center modal-general"
        styleTitle="box-text color-madera fw700 roboto text-center WorkSans"
        show={showModal}
        handleClose={handleCloseModal}
      >
        <div className="container">
          {step === 1 ? (
            <form onSubmit={handleSaveAgenda}>
              <div className="d-flex justify-content-center gap-3 mb-3">
                <div className="form-check">
                  <input
                    type="radio"
                    id="agenda"
                    name="tipoNota"
                    value="agenda"
                    className="form-check-input"
                    checked={tipo === "agenda"}
                    onChange={(e) => setTipo(e.target.value)}
                  />
                  <label htmlFor="agenda" className="form-check-label fw600">
                    Agenda
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    id="recordatorio"
                    name="tipoNota"
                    value="recordatorio"
                    className="form-check-input"
                    checked={tipo === "recordatorio"}
                    onChange={(e) => setTipo(e.target.value)}
                  />
                  <label
                    htmlFor="recordatorio"
                    className="form-check-label fw600"
                  >
                    Recordatorio
                  </label>
                </div>
              </div>

              {tipo === "agenda" && (
                <>
                  <InputComponent
                    label="Nombre"
                    placeholder="Nombre o Apodo"
                    styleLabel="fw600 WorkSans"
                    htmlFor="nombre"
                    type="text"
                    name="nombre"
                    value={formDataAgenda.nombre}
                    onChange={handleInputChangeAgenda}
                  />
                  {errors.nombre && (
                    <p className="text-red-500 text-sm font-semibold mt-1">
                      {errors.nombre}
                    </p>
                  )}
                  <InputComponent
                    label="Celular"
                    placeholder="Celular"
                    styleLabel="fw600 WorkSans"
                    htmlFor="celular"
                    type="text"
                    name="celular"
                    value={formDataAgenda.celular}
                    onChange={handleInputChangeAgenda}
                  />
                </>
              )}

              {tipo === "recordatorio" && (
                <InputComponent
                  label="Titulo"
                  placeholder="Ingrese el título"
                  styleLabel="fw600 WorkSans"
                  htmlFor="nombre"
                  type="text"
                  name="nombre"
                  value={formDataAgenda.nombre}
                  onChange={handleInputChangeAgenda}
                />
              )}
              {errors.nombre && (
                <p className="text-red-500 text-sm font-semibold mt-1">
                  {errors.nombre}
                </p>
              )}
              <div className="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className={`btn ${stylebtn.btn} ${stylebtn.danger}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`btn ${stylebtn.btn} ${stylebtn.primary} ms-3`}
                >
                  {showEdit ? "Guardar" : "Siguiente"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSaveActividad}>
              {(tipo === "agenda" || tipo === "recordatorio") && (
                <>
                  {tipo === "agenda" && (
                    <MoneyInputComponent
                      label="Monto"
                      placeholder="Monto"
                      styleLabel="fw600 WorkSans"
                      htmlFor="monto"
                      name="monto"
                      value={formDataActividad.monto}
                      onChange={handleInputChangeActividad}
                    />
                  )}
                  <textarea
                    name="descripcion"
                    placeholder="Descripción"
                    className="form-control mt-3"
                    rows={3}
                    value={formDataActividad.descripcion}
                    onChange={handleInputChangeActividad}
                  ></textarea>
                  {errors.descripcion && (
                    <p className="text-red-500 text-sm font-semibold mt-1">
                      {errors.descripcion}
                    </p>
                  )}
                  <InputComponent
                    label="Fecha vencimiento"
                    placeholder="Fecha"
                    styleLabel="fw600 WorkSans"
                    htmlFor="fecha"
                    type="date"
                    name="fecha_caducidad"
                    value={formDataActividad.fecha_caducidad}
                    onChange={handleInputChangeActividad}
                  />
                </>
              )}
              {errors.fecha_caducidad && (
                <p className="text-red-500 text-sm font-semibold mt-1">
                  {errors.fecha_caducidad}
                </p>
              )}
              <div className="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className={`btn ${stylebtn.btn} ${stylebtn.danger}`}
                >
                  Atrás
                </button>
                <button
                  type="submit"
                  className={`btn ${stylebtn.btn} ${stylebtn.primary} ms-3`}
                >
                  Guardar
                </button>
              </div>
            </form>
          )}
        </div>
      </ModalGeneralComponent>

      {/* Modal eliminar */}
      <ModalGeneralComponent
        show={showEliminar}
        handleClose={handleCloseEliminar}
        styleGroup="d-flex align-items-center modal-general"
        styleTitle="box-text color-madera fw700 roboto text-center WorkSans"
      >
        <form>
          <TitleH2Component styleGroup="box-text color-madera fw700 roboto mb-4 text-center">
            ¿Seguro que quieres eliminar el registro?
          </TitleH2Component>
          <div className="d-flex justify-content-end">
            <button
              type="button"
              onClick={handleCloseEliminar}
              className={`btn ${stylebtn.btn} ${stylebtn.danger}`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={eliminarRegistro}
              className={`btn ${stylebtn.btn} ${stylebtn.primary} ms-3`}
            >
              SI
            </button>
          </div>
        </form>
      </ModalGeneralComponent>

      {/* Modal login */}
      <section>
        <ModalGeneralComponent
          show={showModalLogin}
          ModalHeader
          titulo={`LOGIN NOTAS PRIVADAS`}
          handleClose={handleCloseModalLogin}
          styleGroup="d-flex align-items-center modal-general modal-login"
          styleTitle="box-text color-madera fw700 roboto text-center WorkSans"
        >
          {loading ? (
            <LoaderComponent styleGruop="h-auto" />
          ) : (
            <form onSubmit={loginPin}>
              <ParrafoComponent styleGroup="text-center color-danger fw400 WorkSans">
                {mensaje}
              </ParrafoComponent>
              <InputComponent
                label="Pin"
                placeholder="Ingrese el pin"
                styleLabel="fw600 WorkSans"
                styleinput="mb-2"
                htmlFor="pin"
                type="password"
                name="pin"
                value={pin}
                onChange={handleChangePin}
              />

              <Link to={"/cambiarpin"}>Cambiar Pin</Link>
              <div className="d-flex justify-content-end mt-3">
                <button
                  type="button"
                  onClick={handleCloseModalLogin}
                  className={`btn ${stylebtn.btn} ${stylebtn.danger}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`btn ${stylebtn.btn} ${stylebtn.primary} ms-3`}
                >
                  Ok
                </button>
              </div>
            </form>
          )}
        </ModalGeneralComponent>
      </section>
    </Fragment>
  );
};

export default NotasAgregadasPages;
